﻿import React from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  VideoCameraIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { AgendaTimeGridView } from '../../components/centralAmparo/AgendaTimeGridView'
import { AppointmentFormModal } from '../../components/centralAmparo/AppointmentFormModal'
import { AppointmentAttendModal } from '../../components/centralAmparo/AppointmentAttendModal'
import { AppointmentAttachmentsList } from '../../components/centralAmparo/AppointmentAttachmentsList'
import { AppointmentPatientHistoryModal } from '../../components/centralAmparo/AppointmentPatientHistoryModal'
import {
  TriagemDetailModal,
  type TriagemDetailAppointmentHint,
} from '../../components/centralAmparo/TriagemDetailModal'
import { TriagemCaseModal } from '../../components/centralAmparo/TriagemCaseModal'
import type { SupportCenterHistoryItem } from '../../types/supportCenterReports'
import {
  getAgendaRange,
  formatAgendaLabel,
  shiftCalendarAnchor,
  type AgendaCalendarView,
} from '../../components/centralAmparo/agendaHelpers'
import { AgendaCancelModal } from '../../components/centralAmparo/AgendaCancelModal'
import { AgendaAttentionModal } from '../../components/centralAmparo/AgendaAttentionModal'
import type { SupportCenterAppointmentCancelChannel } from '../../types/supportCenterAppointments'
import { appointmentChannelLabel } from '../../types/supportCenterAppointments'
import {
  CALENDARIO_MODULE_NAME,
  DEFAULT_STATUS_FILTER,
  DEFAULT_TRIAGE_STATUS_FILTER,
  DETAIL_ACTION_DANGER_CLASS,
  DETAIL_ACTION_DANGER_SOLID_CLASS,
  DETAIL_ACTION_HISTORY_CLASS,
  DETAIL_ACTION_LINK_CLASS,
  DETAIL_ACTION_OUTLINE_CLASS,
  DETAIL_ACTION_RESCHEDULE_CLASS,
  DETAIL_ACTION_SOLID_CLASS,
  DETAIL_ACTION_SUCCESS_CLASS,
  STATUS_FILTER_OPTIONS,
  TRIAGE_STATUS_FILTER_OPTIONS,
  agendaEventAppointmentBadgeClass,
  agendaEventColorLabel,
  agendaEventSwatchClass,
  matchesAgendaPatientQuery,
  matchesAgendaTriageFilter,
  resolveAgendaHourRange,
  summarizeAgendaStatuses,
  summarizeSlotOccupancy,
  type AgendaTriageStatusFilter,
} from '../../components/centralAmparo/calendarioAgendaHelpers'
import {
  buildOccupancyFromApiSlots,
  buildOccupancyLocal,
  type SlotOccupancyMap,
} from '../../components/centralAmparo/agendaOccupancyHelpers'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import {
  attendanceEarlyMessage,
  attendanceFormEarlyMessage,
  attendanceFormPastMessage,
  attendancePastMessage,
  attendanceRescheduledMessage,
  callLinkPastMessage,
  canOpenCallLink,
  canPerformAttendance,
  isAppointmentBeforeScheduled,
  isAppointmentPastForAttendance,
  triagePastMessage,
} from '../../services/supportCenterAttendance'
import {
  enrichAgendaEventsFormSubmitted,
} from '../../services/supportCenterAttend'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import { supportCenterTriageService } from '../../services/supportCenterTriage'
import { supportCenterTriageCatalogService } from '../../services/supportCenterTriageCatalog'
import {
  isSlotBookable,
  supportCenterParametersService,
} from '../../services/supportCenterParameters'
import { supportCenterCancelReasonsService } from '../../services/supportCenterCancelReasons'
import type {
  SupportCenterAgendaEvent,
  SupportCenterAttendantOption,
} from '../../types/supportCenterAppointments'
import type {
  SupportCenterProfileOption,
  SupportCenterSpecialization,
} from '../../types/supportCenterSpecialties'
import {
  ASSISTANCE_FORM_KIND_MISSING,
  SUPPORT_CENTER_FORM_KIND_LABELS,
  appointmentFormKind,
  type SupportCenterUserAvailability,
  type SupportCenterUserUnavailability,
  type SupportCenterHoliday,
} from '../../types/supportCenterSpecialties'
import type { SupportCenterCancelReason } from '../../types/supportCenterCancelReasons'
import {
  DEFAULT_SCHEDULING_LIMITS,
  type SupportCenterSchedulingLimits,
} from '../../types/supportCenterParameters'
import type {
  SupportCenterAppointmentStatus,
  SupportCenterDemandType,
  SupportCenterReferralOrigin,
  SupportCenterTriageListItem,
} from '../../types/supportCenterTriage'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import { CheckboxMultiSelect } from '../Qualidade/CheckboxMultiSelect'
import { triageColumnBadgeClass } from '../../components/centralAmparo/triagemHelpers'
import {
  DASHBOARD_INPUT_CLASS,
  ATTENDANCE_FORM_STATUS_LABELS,
  appointmentStatusBadgeClass,
  attendanceFormStatusBadgeClass,
  attendanceFormStatusFromEvent,
  formatCpf,
  formatDateTime,
  formatDateTimeLines,
  formatPhone,
  getErrorMessage,
  phoneTelHref,
} from '../../components/centralAmparo/dashboardHelpers'
import {
  buildIsoDate,
  formatDateBr,
} from '../../components/centralAmparo/scheduleHelpers'
import {
  clampDay,
  todayParts,
} from '../../components/centralAmparo/parametrosHelpers'
import { formatDateOnlyPtBr, nowTimestampSaoPaulo } from '../../utils/dateOnly'
import ParametrosUnavailModal, {
  type UnavailModalState,
} from './ParametrosUnavailModal'

const MODULE_NAME = CALENDARIO_MODULE_NAME

type HeaderAction = {
  visible: boolean
  disabled: boolean
  onClick: () => void
}

type CalendarioAgendaTabProps = {
  onCreateActionChange?: (action: HeaderAction | null) => void
  onUnavailabilityActionChange?: (action: HeaderAction | null) => void
}

const CalendarioAgendaTab: React.FC<CalendarioAgendaTabProps> = ({
  onCreateActionChange,
  onUnavailabilityActionChange,
}) => {
  const { user, profile } = useAuthContext()
  const actorId = user?.id || profile?.id || null
  const { logCreate, logUpdate, logView, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    MODULE_NAME,
    'view_values'
  )

  const [viewMode, setViewMode] = React.useState<AgendaCalendarView>('week')
  const [calendarAnchor, setCalendarAnchor] = React.useState(() => new Date())
  const [scrollToNowToken, setScrollToNowToken] = React.useState(0)
  /** Pedido pendente para repetir a ação do botão Hoje (data de hoje + horário atual). */
  const pendingFocusNowRef = React.useRef(true)
  /** Espera o reload da agenda terminar antes de centralizar o horário. */
  const awaitingLoadCycleRef = React.useRef(false)
  /** Evita rolar de novo ao remontar a grade no mesmo pedido (troca de semana, filtro). */
  const scrollSessionRef = React.useRef(0)
  const [formSpecializations, setFormSpecializations] = React.useState<SupportCenterSpecialization[]>(
    []
  )
  const [userSpecializationIds, setUserSpecializationIds] = React.useState<string[]>([])
  const [filterSpecializations, setFilterSpecializations] = React.useState<
    SupportCenterSpecialization[]
  >([])
  const [attendants, setAttendants] = React.useState<SupportCenterAttendantOption[]>([])
  const [scopeReady, setScopeReady] = React.useState(false)
  const [scopeError, setScopeError] = React.useState<string | null>(null)
  const [refreshKey, setRefreshKey] = React.useState(0)

  const [formOpen, setFormOpen] = React.useState(false)
  const [formMode, setFormMode] = React.useState<'create' | 'edit'>('create')
  const [formInitial, setFormInitial] = React.useState<SupportCenterAgendaEvent | null>(null)
  const [slotScheduledAt, setSlotScheduledAt] = React.useState<string | undefined>()

  const [selected, setSelected] = React.useState<SupportCenterAgendaEvent | null>(null)
  const [attendOpen, setAttendOpen] = React.useState(false)
  const [attendStartConfirmOpen, setAttendStartConfirmOpen] = React.useState(false)
  const [attendStartConfirming, setAttendStartConfirming] = React.useState(false)
  const [attendStartError, setAttendStartError] = React.useState<string | null>(null)
  const [attendStartRecordedAt, setAttendStartRecordedAt] = React.useState<string | null>(null)
  const [attendFinalizeConfirmOpen, setAttendFinalizeConfirmOpen] = React.useState(false)
  const [attendFinalizeConfirming, setAttendFinalizeConfirming] = React.useState(false)
  const [attendFinalizeError, setAttendFinalizeError] = React.useState<string | null>(null)
  const [attendFinalizeRecordedAt, setAttendFinalizeRecordedAt] = React.useState<string | null>(
    null
  )
  const [discardPreConfirmOpen, setDiscardPreConfirmOpen] = React.useState(false)
  const [discardPreConfirming, setDiscardPreConfirming] = React.useState(false)
  const [discardPreError, setDiscardPreError] = React.useState<string | null>(null)
  const [reasonModalKind, setReasonModalKind] = React.useState<'cancel' | null>(null)
  const [cancelReasonId, setCancelReasonId] = React.useState('')
  const [cancellationChannel, setCancellationChannel] = React.useState<
    '' | SupportCenterAppointmentCancelChannel
  >('')
  const [cancelReasons, setCancelReasons] = React.useState<SupportCenterCancelReason[]>([])
  const [loadingCancelReasons, setLoadingCancelReasons] = React.useState(false)
  const [cancelling, setCancelling] = React.useState(false)
  const [cancelError, setCancelError] = React.useState<string | null>(null)
  const [detailModalOpen, setDetailModalOpen] = React.useState(false)
  const [triageDetail, setTriageDetail] = React.useState<SupportCenterTriageListItem | null>(null)
  const [detailAppointmentHint, setDetailAppointmentHint] =
    React.useState<TriagemDetailAppointmentHint | null>(null)
  const [triageDetailLoading, setTriageDetailLoading] = React.useState(false)
  const [triageDetailError, setTriageDetailError] = React.useState<string | null>(null)
  const [triageCaseModal, setTriageCaseModal] = React.useState<SupportCenterTriageListItem | null>(
    null
  )
  const [triageCaseLoading, setTriageCaseLoading] = React.useState(false)
  const [triageProfiles, setTriageProfiles] = React.useState<SupportCenterProfileOption[]>([])
  const [triageReferralOrigins, setTriageReferralOrigins] = React.useState<
    SupportCenterReferralOrigin[]
  >([])
  const [triageDemandTypes, setTriageDemandTypes] = React.useState<SupportCenterDemandType[]>([])
  const [triageCatalogsLoaded, setTriageCatalogsLoaded] = React.useState(false)
  const [historyOpen, setHistoryOpen] = React.useState(false)
  const [callLinkMenuOpen, setCallLinkMenuOpen] = React.useState(false)
  const callLinkMenuRef = React.useRef<HTMLDivElement>(null)
  const [totalsOpen, setTotalsOpen] = React.useState(false)
  const totalsButtonRef = React.useRef<HTMLButtonElement>(null)
  const totalsPopoverRef = React.useRef<HTMLDivElement>(null)
  const [totalsPopoverPos, setTotalsPopoverPos] = React.useState<{
    top: number
    right: number
  } | null>(null)

  const [events, setEvents] = React.useState<SupportCenterAgendaEvent[]>([])
  const [filterAttendantEmail, setFilterAttendantEmail] = React.useState('')
  const [filterSpecializationId, setFilterSpecializationId] = React.useState('')
  const [filterStatus, setFilterStatus] =
    React.useState<SupportCenterAppointmentStatus[]>(DEFAULT_STATUS_FILTER)
  const [filterTriageStatus, setFilterTriageStatus] =
    React.useState<AgendaTriageStatusFilter[]>(DEFAULT_TRIAGE_STATUS_FILTER)
  const [filterPatientQuery, setFilterPatientQuery] = React.useState('')
  const [debouncedPatientQuery, setDebouncedPatientQuery] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [schedulingLimits, setSchedulingLimits] =
    React.useState<SupportCenterSchedulingLimits>(DEFAULT_SCHEDULING_LIMITS)
  const [hourStart, setHourStart] = React.useState(7)
  const [hourEnd, setHourEnd] = React.useState(20)
  const [availabilities, setAvailabilities] = React.useState<SupportCenterUserAvailability[]>([])
  const [unavailabilities, setUnavailabilities] = React.useState<
    SupportCenterUserUnavailability[]
  >([])
  const [holidays, setHolidays] = React.useState<SupportCenterHoliday[]>([])
  const [slotOccupancy, setSlotOccupancy] = React.useState<SlotOccupancyMap | null>(null)

  const [unavailModal, setUnavailModal] = React.useState<UnavailModalState | null>(null)
  const [unavailProfileOptions, setUnavailProfileOptions] = React.useState<
    SupportCenterProfileOption[]
  >([])
  const [submittingUnavail, setSubmittingUnavail] = React.useState(false)
  const [unavailError, setUnavailError] = React.useState<string | null>(null)

  const selectedRef = React.useRef(selected)
  selectedRef.current = selected

  React.useEffect(() => {
    const handler = window.setTimeout(() => setDebouncedPatientQuery(filterPatientQuery), 350)
    return () => window.clearTimeout(handler)
  }, [filterPatientQuery])

  React.useEffect(() => {
    void supportCenterParametersService.getSchedulingLimits().then(setSchedulingLimits)
  }, [])

  React.useEffect(() => {
    let cancelled = false
    const loadScope = async () => {
      if (loadingViewValues) return
      if (!actorId) {
        setFormSpecializations([])
        setUserSpecializationIds([])
        setFilterSpecializations([])
        setAttendants([])
        setAvailabilities([])
        setUnavailabilities([])
        setHolidays([])
        setScopeError(null)
        setScopeReady(true)
        const fallback = resolveAgendaHourRange([])
        setHourStart(fallback.start)
        setHourEnd(fallback.end)
        return
      }
      setScopeReady(false)
      setScopeError(null)
      try {

        const [allSpecs, availRows, unavailRows, holidayRows, ownSpecIds] = await Promise.all([
          supportCenterSpecialtiesService.listSpecializations(),
          supportCenterSpecialtiesService.listAvailability(),
          supportCenterSpecialtiesService.listUnavailability(),
          supportCenterSpecialtiesService.listHolidays(),
          canViewValues
            ? Promise.resolve([] as string[])
            : supportCenterSpecialtiesService.listSpecializationIdsForUser(actorId),
        ])

        if (cancelled) return
        setAvailabilities(availRows)
        setUnavailabilities(unavailRows)
        setHolidays(holidayRows)
        // Modal: catálogo completo; escopo fino via ver valores + elegibilidade do CPF.
        setFormSpecializations(allSpecs)
        setUserSpecializationIds(canViewValues ? [] : ownSpecIds)
        if (canViewValues) {
          setFilterSpecializations(allSpecs)
          const range = resolveAgendaHourRange(availRows.map((row) => row.hours))
          setHourStart(range.start)
          setHourEnd(range.end)
        } else {
          setFilterSpecializations([])
          setAttendants([])
          const mine = availRows.find((row) => row.user_id === actorId)
          const range = resolveAgendaHourRange(mine ? [mine.hours] : [])
          setHourStart(range.start)
          setHourEnd(range.end)
        }
      } catch (e) {
        if (!cancelled) setScopeError(getErrorMessage(e))
      } finally {
        if (!cancelled) setScopeReady(true)
      }
    }
    void loadScope()
    return () => {
      cancelled = true
    }
  }, [actorId, canViewValues, loadingViewValues])

  const bumpRefresh = React.useCallback(() => {
    setRefreshKey((key) => key + 1)
  }, [])

  const shiftAnchor = React.useCallback((delta: number) => {
    setCalendarAnchor((prev) => shiftCalendarAnchor(prev, viewMode, delta))
  }, [viewMode])

  const goToNow = React.useCallback(() => {
    pendingFocusNowRef.current = true
    if (!loading) awaitingLoadCycleRef.current = true
    setCalendarAnchor(new Date())
  }, [loading])

  const refreshAgenda = React.useCallback(() => {
    goToNow()
    bumpRefresh()
  }, [goToNow, bumpRefresh])

  React.useLayoutEffect(() => {
    if (!pendingFocusNowRef.current) return
    if (!scopeReady || loadingViewValues) return
    if (awaitingLoadCycleRef.current) {
      if (loading) awaitingLoadCycleRef.current = false
      return
    }
    if (loading) return
    pendingFocusNowRef.current = false
    setScrollToNowToken((token) => token + 1)
  }, [loading, scopeReady, loadingViewValues])

  const gridDays = React.useMemo(() => {
    const { start } = getAgendaRange(calendarAnchor, viewMode)
    if (viewMode === 'day') return [start]
    return Array.from({ length: 5 }, (_, i) => {
      const day = new Date(start)
      day.setDate(day.getDate() + i)
      return day
    })
  }, [calendarAnchor, viewMode])

  const prevNavLabel = viewMode === 'day' ? 'Dia anterior' : 'Semana anterior'
  const nextNavLabel = viewMode === 'day' ? 'Próximo dia' : 'Próxima semana'
  const goToDateTitle =
    viewMode === 'day' ? 'Ir para este dia' : 'Ir para a semana desta data'

  const openCreate = React.useCallback(
    (isoLocal?: string) => {
      if (isoLocal && !isSlotBookable(isoLocal, schedulingLimits)) return
      setFormMode('create')
      setFormInitial(null)
      setSlotScheduledAt(isoLocal)
      setFormOpen(true)
    },
    [schedulingLimits]
  )

  const openNewUnavail = React.useCallback(async () => {
    if (!actorId) return
    setUnavailError(null)
    const start = todayParts()
    const lockProfessional = !canViewValues

    try {
      if (lockProfessional) {
        setUnavailProfileOptions([
          { id: actorId, name: profile?.name?.trim() || 'Você' },
        ])
      } else {
        const assigns = await supportCenterSpecialtiesService.listAssignments()
        const byId = new Map<string, SupportCenterProfileOption>()
        for (const a of assigns) {
          if (a.profile?.id) {
            byId.set(a.profile.id, { id: a.profile.id, name: a.profile.name })
          }
        }
        setUnavailProfileOptions(
          Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        )
      }
    } catch (e) {
      setUnavailError(getErrorMessage(e))
      setUnavailProfileOptions(
        lockProfessional
          ? [{ id: actorId, name: profile?.name?.trim() || 'Você' }]
          : []
      )
    }

    setUnavailModal({
      user_id: lockProfessional ? actorId : '',
      start,
      end: { ...start },
      spanDays: false,
      all_day: true,
      hours: [],
      reason: '',
    })
  }, [actorId, canViewValues, profile?.name])

  const closeUnavailModal = React.useCallback(() => {
    if (submittingUnavail) return
    setUnavailModal(null)
    setUnavailError(null)
  }, [submittingUnavail])

  const handleUnavailSave = React.useCallback(async () => {
    if (!unavailModal || !actorId) return
    if (!unavailModal.user_id) {
      setUnavailError('Selecione o profissional.')
      return
    }

    const start = clampDay(unavailModal.start)
    const end = unavailModal.spanDays ? clampDay(unavailModal.end) : { ...start }
    const startsOn = buildIsoDate(start.year, start.month, start.day)
    const endsOn = buildIsoDate(end.year, end.month, end.day)

    if (unavailModal.spanDays && endsOn < startsOn) {
      setUnavailError('A data final deve ser maior ou igual à data inicial.')
      return
    }
    if (!unavailModal.all_day && unavailModal.hours.length === 0) {
      setUnavailError('Selecione ao menos uma hora ou marque dia(s) inteiro(s).')
      return
    }

    setSubmittingUnavail(true)
    setUnavailError(null)
    try {
      const savedId = await supportCenterSpecialtiesService.upsertUnavailability({
        id: unavailModal.id,
        user_id: unavailModal.user_id,
        starts_on: startsOn,
        ends_on: endsOn,
        all_day: unavailModal.all_day,
        hours: unavailModal.hours,
        reason: unavailModal.reason,
        actor_id: actorId,
      })
      const profileName =
        unavailProfileOptions.find((p) => p.id === unavailModal.user_id)?.name ??
        unavailModal.user_id
      const periodLabel =
        startsOn === endsOn
          ? formatDateBr(startsOn)
          : `${formatDateBr(startsOn)} a ${formatDateBr(endsOn)}`
      void logCreate(
        'support_center_user_unavailability',
        savedId,
        `Criou indisponibilidade: ${profileName} (${periodLabel})`
      )
      try {
        const unavailRows = await supportCenterSpecialtiesService.listUnavailability()
        setUnavailabilities(unavailRows)
      } catch {
        // A ocupação via API ainda atualiza com bumpRefresh.
      }
      setUnavailModal(null)
      setUnavailError(null)
      bumpRefresh()
    } catch (e) {
      setUnavailError(getErrorMessage(e))
    } finally {
      setSubmittingUnavail(false)
    }
  }, [unavailModal, actorId, unavailProfileOptions, logCreate, bumpRefresh])

  const openEdit = React.useCallback((event: SupportCenterAgendaEvent) => {
    setFormMode('edit')
    setFormInitial(event)
    setSlotScheduledAt(undefined)
    setFormOpen(true)
  }, [])

  /** Completar/confirmar pré: abre o form de create hidratado com o hold. */
  const openConfirmPreBooking = React.useCallback((event: SupportCenterAgendaEvent) => {
    setFormMode('create')
    setFormInitial(event)
    setSlotScheduledAt(undefined)
    setFormOpen(true)
  }, [])

  const handleSaved = async (event: SupportCenterAgendaEvent) => {
    if (formMode === 'create') {
      if (event.status === 'pre_agendado') {
        void logCreate(
          'support_center_appointment',
          event.id,
          'Criou pré-agendamento na Central de Amparo'
        )
      } else {
        void logCreate(
          'support_center_appointment',
          event.id,
          'Criou agendamento na Central de Amparo'
        )
      }
    } else {
      void logUpdate(
        'support_center_appointment',
        event.id,
        'Reagendou agendamento na Central de Amparo (POST+DELETE)'
      )
    }
    setSelected(event)
    bumpRefresh()
  }

  const openCancelModal = React.useCallback(() => {
    if (!selected || !canDelete) return
    setCancelReasonId('')
    setCancellationChannel('')
    setCancelError(null)
    setReasonModalKind('cancel')
    setLoadingCancelReasons(true)
    void supportCenterCancelReasonsService
      .listByKind('atendimento')
      .then((list) => {
        setCancelReasons(list)
        if (list.length === 0) {
          setCancelError(
            'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Atendimento.'
          )
        }
      })
      .catch((e) => {
        setCancelReasons([])
        setCancelError(
          e instanceof Error ? e.message : 'Não foi possível carregar os motivos.'
        )
      })
      .finally(() => setLoadingCancelReasons(false))
  }, [selected, canDelete])

  const openTriageDetail = React.useCallback(async () => {
    if (!selected?.triage_id || selected.triage_status !== 'concluida') return
    if (!appointmentFormKind(selected)) {
      setTriageDetailError(ASSISTANCE_FORM_KIND_MISSING)
      return
    }
    setTriageDetailLoading(true)
    setTriageDetailError(null)
    setDetailAppointmentHint({
      appointment_id: selected.id,
      specialization_name: selected.specialization?.name ?? null,
      appointment_status: selected.status,
      triage_status_label: selected.triage_status
        ? TRIAGE_STATUS_LABELS[selected.triage_status]
        : null,
      scheduled_at: selected.scheduled_at,
      form_kind: appointmentFormKind(selected),
    })
    try {
      const detail = await supportCenterTriageService.getCaseById(
        selected.triage_id,
        selected.id
      )
      if (!detail) {
        setTriageDetailError('Triagem não encontrada.')
        setTriageDetail(null)
        return
      }
      setTriageDetail(detail)
      setDetailModalOpen(true)
      void logView('support_center_triage', selected.triage_id, 'Visualizou triagem a partir do calendário')
    } catch (e) {
      setTriageDetail(null)
      setTriageDetailError(getErrorMessage(e))
    } finally {
      setTriageDetailLoading(false)
    }
  }, [selected, logView])

  const openHistoryRowDetail = React.useCallback(
    async (row: SupportCenterHistoryItem) => {
      setHistoryOpen(false)
      setTriageDetailError(null)
      setDetailAppointmentHint({
        appointment_id: row.appointment_id,
        specialization_name: row.specialization_name,
        appointment_status: row.status,
        triage_status_label: row.triage_status ? TRIAGE_STATUS_LABELS[row.triage_status] : null,
        scheduled_at: row.scheduled_at,
        form_kind: row.form_kind,
      })

      if (!row.triage_id) {
        setTriageDetail(null)
        setDetailModalOpen(true)
        return
      }

      setTriageDetailLoading(true)
      try {
        const detail = await supportCenterTriageService.getCaseById(
          row.triage_id,
          row.appointment_id
        )
        setTriageDetail(detail)
        setDetailModalOpen(true)
        if (detail) {
          void logView(
            'support_center_triage',
            row.triage_id,
            `Visualizou detalhe a partir do histórico (${row.triage_status ?? 'sem status'})`
          )
        } else {
          setTriageDetailError('Triagem não encontrada.')
        }
      } catch (e) {
        setTriageDetail(null)
        setTriageDetailError(getErrorMessage(e))
        setDetailModalOpen(true)
      } finally {
        setTriageDetailLoading(false)
      }
    },
    [logView]
  )

  const closeTriageDetail = () => {
    if (triageDetailLoading) return
    setDetailModalOpen(false)
    setTriageDetail(null)
    setDetailAppointmentHint(null)
    setTriageDetailError(null)
  }

  const openRealizarTriagem = React.useCallback(async () => {
    if (!selected?.triage_id) {
      setTriageDetailError('Triagem ainda não vinculada a este caso (paciente + assistência).')
      return
    }
    if (!appointmentFormKind(selected)) {
      setTriageDetailError(ASSISTANCE_FORM_KIND_MISSING)
      return
    }
    if (
      isAppointmentPastForAttendance(
        selected.scheduled_at,
        schedulingLimits.pastToleranceMinutes
      )
    ) {
      setActionError(triagePastMessage(schedulingLimits.pastToleranceMinutes))
      return
    }
    if (triageCaseLoading) return
    setTriageCaseLoading(true)
    setTriageDetailError(null)
    try {
      const catalogPromise = triageCatalogsLoaded
        ? Promise.resolve({
            profiles: triageProfiles,
            origins: triageReferralOrigins,
            demands: triageDemandTypes,
          })
        : Promise.all([
            supportCenterSpecialtiesService.listTriageStaffProfiles(),
            supportCenterTriageCatalogService.listReferralOrigins(),
            supportCenterTriageCatalogService.listDemandTypes(),
          ]).then(([profiles, origins, demands]) => ({ profiles, origins, demands }))

      const [detail, catalogs] = await Promise.all([
        supportCenterTriageService.getCaseById(selected.triage_id, selected.id),
        catalogPromise,
      ])

      if (!triageCatalogsLoaded) {
        setTriageProfiles(catalogs.profiles)
        setTriageReferralOrigins(catalogs.origins)
        setTriageDemandTypes(catalogs.demands)
        setTriageCatalogsLoaded(true)
      }

      if (!detail) {
        setTriageDetailError('Triagem não encontrada.')
        return
      }
      setTriageCaseModal(detail)
      void logView('support_center_triage', selected.triage_id, 'Abriu realizar triagem a partir do calendário')
    } catch (e) {
      setTriageDetailError(getErrorMessage(e))
    } finally {
      setTriageCaseLoading(false)
    }
  }, [
    selected,
    triageCaseLoading,
    triageCatalogsLoaded,
    triageProfiles,
    triageReferralOrigins,
    triageDemandTypes,
    schedulingLimits.pastToleranceMinutes,
    logView,
  ])

  const closeTriageCaseModal = React.useCallback(() => {
    setTriageCaseModal(null)
  }, [])

  const handleTriageCaseSaved = React.useCallback(async () => {
    setTriageCaseModal(null)
    bumpRefresh()
  }, [bumpRefresh])

  const closeCancelModal = () => {
    if (cancelling) return
    setReasonModalKind(null)
    setCancelError(null)
    setCancellationChannel('')
  }

  const openDiscardPreConfirm = React.useCallback(() => {
    if (!selected || selected.status !== 'pre_agendado' || !canDelete) return
    setDiscardPreError(null)
    setDiscardPreConfirmOpen(true)
  }, [selected, canDelete])

  const handleDiscardPreBooking = React.useCallback(async () => {
    if (!selected || selected.status !== 'pre_agendado' || !actorId || !canDelete) return
    if (discardPreConfirming) return
    setDiscardPreConfirming(true)
    setDiscardPreError(null)
    try {
      await supportCenterAppointmentsService.cancelPreBooking(selected.id, actorId)
      void logDelete(
        'support_center_appointment',
        selected.id,
        'Descartou pré-agendamento na Central de Amparo'
      )
      setDiscardPreConfirmOpen(false)
      setSelected(null)
      bumpRefresh()
    } catch (e) {
      setDiscardPreError(getErrorMessage(e))
    } finally {
      setDiscardPreConfirming(false)
    }
  }, [selected, actorId, canDelete, discardPreConfirming, logDelete, bumpRefresh])

  const handleCancelConfirm = async () => {
    if (!selected || !actorId || !canDelete || cancelling || !reasonModalKind) return
    const selectedReason = cancelReasons.find((r) => r.id === cancelReasonId)
    if (!selectedReason) {
      setCancelError(
        cancelReasons.length === 0
          ? 'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Atendimento.'
          : 'Selecione o motivo.'
      )
      return
    }
    if (
      cancellationChannel !== 'whatsapp' &&
      cancellationChannel !== 'telefone' &&
      cancellationChannel !== 'videochamada'
    ) {
      setCancelError('Selecione o canal do cancelamento.')
      return
    }
    setCancelling(true)
    setCancelError(null)
    try {
      await supportCenterAppointmentsService.cancel(
        selected.id,
        selectedReason.id,
        actorId,
        cancellationChannel
      )
      void logDelete('support_center_appointment', selected.id, 'Cancelou agendamento na Central de Amparo')
      setSelected(null)
      setReasonModalKind(null)
      setCancelError(null)
      setCancellationChannel('')
      bumpRefresh()
    } catch (e) {
      setCancelError(
        e instanceof Error ? e.message : 'Não foi possível cancelar. Tente novamente.'
      )
    } finally {
      setCancelling(false)
    }
  }

  const pastToleranceMinutes = schedulingLimits.pastToleranceMinutes
  const earlyStartMinutes = schedulingLimits.earlyStartMinutes
  const attendancePastMsg = attendancePastMessage(pastToleranceMinutes)
  const attendanceEarlyMsg = attendanceEarlyMessage(earlyStartMinutes)
  const formPastMsg = attendanceFormPastMessage(pastToleranceMinutes)
  const formEarlyMsg = attendanceFormEarlyMessage(earlyStartMinutes)
  const callLinkPastMsg = callLinkPastMessage(pastToleranceMinutes)

  const attendanceRescheduledMsg = attendanceRescheduledMessage()

  const attendanceBlockedReason = React.useCallback(
    (
      event: SupportCenterAgendaEvent | null,
      kind: 'atendimento' | 'formulario' = 'atendimento'
    ): string => {
      const earlyMsg = kind === 'formulario' ? formEarlyMsg : attendanceEarlyMsg
      const pastMsg = kind === 'formulario' ? formPastMsg : attendancePastMsg
      if (!event) return pastMsg
      if (event.status === 'reagendado') {
        return attendanceRescheduledMsg
      }
      if (event.triage_status !== 'concluida') {
        return kind === 'formulario'
          ? 'Formulário disponível somente após a triagem ser concluída.'
          : 'Disponível somente após a triagem ser concluída.'
      }
      if (isAppointmentBeforeScheduled(event.scheduled_at, earlyStartMinutes)) {
        return earlyMsg
      }
      if (isAppointmentPastForAttendance(event.scheduled_at, pastToleranceMinutes)) {
        return pastMsg
      }
      return pastMsg
    },
    [
      attendanceEarlyMsg,
      attendancePastMsg,
      attendanceRescheduledMsg,
      earlyStartMinutes,
      formEarlyMsg,
      formPastMsg,
      pastToleranceMinutes,
    ]
  )

  const openCallLink = React.useCallback(() => {
    if (!canOpenCallLink(selected, !!canUpdate, pastToleranceMinutes)) {
      setActionError(
        selected?.status === 'reagendado' ? attendanceRescheduledMsg : callLinkPastMsg
      )
      setCallLinkMenuOpen(false)
      return
    }
    const link = selected?.call_link?.trim()
    if (!link) {
      setActionError('Link da chamada ainda não disponível.')
      setCallLinkMenuOpen(false)
      return
    }
    setActionError(null)
    setCallLinkMenuOpen(false)
    window.open(link, '_blank', 'noopener,noreferrer')
  }, [selected, canUpdate, pastToleranceMinutes, attendanceRescheduledMsg, callLinkPastMsg])

  const copyCallLink = React.useCallback(async () => {
    const link = selected?.call_link_short?.trim()
    if (!link) {
      setActionError('Link encurtado ainda não disponível.')
      setCallLinkMenuOpen(false)
      return
    }
    try {
      await navigator.clipboard.writeText(link)
      setActionError(null)
      setCallLinkMenuOpen(false)
    } catch {
      setActionError('Não foi possível copiar o link. Tente novamente.')
      setCallLinkMenuOpen(false)
    }
  }, [selected])

  const toggleCallLinkMenu = React.useCallback(() => {
    if (callLinkMenuOpen) {
      setCallLinkMenuOpen(false)
      return
    }
    if (!canOpenCallLink(selected, !!canUpdate, pastToleranceMinutes)) {
      setActionError(callLinkPastMsg)
      return
    }
    const link = selected?.call_link?.trim()
    if (!link) {
      setActionError('Link da chamada ainda não disponível.')
      return
    }
    setActionError(null)
    setCallLinkMenuOpen(true)
  }, [
    callLinkMenuOpen,
    selected,
    canUpdate,
    pastToleranceMinutes,
    callLinkPastMsg,
  ])

  React.useEffect(() => {
    if (!callLinkMenuOpen) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (callLinkMenuRef.current?.contains(target)) return
      setCallLinkMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCallLinkMenuOpen(false)
    }
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [callLinkMenuOpen])

  const attendanceAlreadyDone =
    selected?.status === 'realizado' || !!selected?.attendance_completed_at
  const formAlreadySubmitted = !!selected?.attendance_form_submitted
  const selectedFormStatus = selected ? attendanceFormStatusFromEvent(selected) : null
  const attendActionLabel = attendanceAlreadyDone
    ? 'Ver o atendimento'
    : selected?.attendance_started_at
      ? 'Finalizar atendimento'
      : 'Iniciar atendimento'

  /** Iniciar (confirmação) ou Finalizar (confirmação) ou Ver (só leitura do form). */
  const openAttendPrimaryAction = React.useCallback(() => {
    if (!selected) return
    const alreadyDone = selected.status === 'realizado' || !!selected.attendance_completed_at
    if (alreadyDone) {
      if (!appointmentFormKind(selected)) {
        setActionError(ASSISTANCE_FORM_KIND_MISSING)
        return
      }
      setActionError(null)
      setAttendOpen(true)
      return
    }
    if (!canPerformAttendance(selected, !!canUpdate, pastToleranceMinutes, earlyStartMinutes)) {
      setActionError(attendanceBlockedReason(selected, 'formulario'))
      return
    }
    setActionError(null)
    if (selected.attendance_started_at) {
      setAttendFinalizeError(null)
      setAttendFinalizeRecordedAt(nowTimestampSaoPaulo())
      setAttendFinalizeConfirmOpen(true)
      return
    }
    setAttendStartError(null)
    setAttendStartRecordedAt(nowTimestampSaoPaulo())
    setAttendStartConfirmOpen(true)
  }, [selected, canUpdate, pastToleranceMinutes, earlyStartMinutes, attendanceBlockedReason])

  /** Abre o formulário só depois do início; não exige conclusão. */
  const openAttendanceForm = React.useCallback(() => {
    if (!selected) return
    if (!appointmentFormKind(selected)) {
      setActionError(ASSISTANCE_FORM_KIND_MISSING)
      return
    }
    const alreadyDone = selected.status === 'realizado' || !!selected.attendance_completed_at
    if (!alreadyDone && !selected.attendance_started_at) {
      setActionError('Formulário disponível somente após iniciar o atendimento.')
      return
    }
    setActionError(null)
    setAttendOpen(true)
  }, [selected])

  const confirmAttendFormStart = React.useCallback(async () => {
    if (!selected || attendStartConfirming) return
    setAttendStartConfirming(true)
    setAttendStartError(null)
    setActionError(null)
    try {
      if (!selected.attendance_started_at) {
        const startedAt = attendStartRecordedAt || nowTimestampSaoPaulo()
        await supportCenterAppointmentsService.markAttendanceStarted(selected.id, startedAt)
        const withStart = <
          T extends {
            id: string
            attendance_started_at?: string | null
            attendance_form_submitted?: boolean | null
          },
        >(
          event: T
        ): T =>
          event.id === selected.id && !event.attendance_started_at
            ? {
                ...event,
                attendance_started_at: startedAt,
                attendance_form_submitted: false,
              }
            : event
        setSelected((prev) => (prev ? withStart(prev) : prev))
        setEvents((prev) => prev.map(withStart))
        void logUpdate(
          'support_center_appointment',
          selected.id,
          'Iniciou atendimento pela agenda da Central de Amparo'
        )
      }
      setAttendStartConfirmOpen(false)
    } catch (e) {
      setAttendStartError(getErrorMessage(e))
    } finally {
      setAttendStartConfirming(false)
    }
  }, [selected, attendStartConfirming, attendStartRecordedAt, logUpdate])

  const confirmAttendFinalize = React.useCallback(async () => {
    if (!selected || attendFinalizeConfirming) return
    setAttendFinalizeConfirming(true)
    setAttendFinalizeError(null)
    setActionError(null)
    try {
      const completedAt = attendFinalizeRecordedAt || nowTimestampSaoPaulo()
      await supportCenterAppointmentsService.markRealizado(selected.id, completedAt)
      void logUpdate(
        'support_center_appointment',
        selected.id,
        'Finalizou atendimento pela agenda da Central de Amparo'
      )
      setAttendFinalizeConfirmOpen(false)
      setSelected(null)
      bumpRefresh()
    } catch (e) {
      setAttendFinalizeError(getErrorMessage(e))
    } finally {
      setAttendFinalizeConfirming(false)
    }
  }, [selected, attendFinalizeConfirming, attendFinalizeRecordedAt, logUpdate, bumpRefresh])

  const openHistory = React.useCallback(() => {
    setHistoryOpen(true)
  }, [])

  const triageApproved = selected?.triage_status === 'concluida'
  const canRealizarTriagem =
    !!selected &&
    (!selected.triage_status || selected.triage_status === 'pendente')
  const canRescheduleSelected =
    !!canUpdate &&
    !!selected &&
    (selected.status === 'marcado' || selected.status === 'reagendado')
  /** Atendimento/formulário só no vigente (`marcado`); `reagendado` é o antigo substituído. */
  const canAttendFlowSelected =
    !!canUpdate && !!selected && selected.status === 'marcado'
  const canCancelSelected =
    !!canDelete &&
    !!selected &&
    (selected.status === 'marcado' || selected.status === 'reagendado')
  /** Cancelar `realizado` (API + e-mail): só ver valores + delete, sem formulário enviado. */
  const canCancelRealizadoSelected =
    !!canViewValues &&
    !!canDelete &&
    !!selected &&
    selected.status === 'realizado' &&
    !selected.attendance_form_submitted
  const canConfirmPreBookingSelected =
    !!canUpdate && !!selected && selected.status === 'pre_agendado'
  const canDiscardPreBookingSelected =
    !!canDelete && !!selected && selected.status === 'pre_agendado'
  const selectedFormKind = appointmentFormKind(selected)
  const historyActionVisible = !!(
    selectedFormKind &&
    selected?.patient_cpf &&
    selected.status !== 'pre_agendado'
  )
  const detailActionCount =
    (canRescheduleSelected ? 2 : 0) +
    (canConfirmPreBookingSelected ? 1 : 0) +
    (historyActionVisible ? 1 : 0) +
    (canCancelSelected || canCancelRealizadoSelected || canDiscardPreBookingSelected ? 1 : 0)
  const selectedPhoneLabel = selected ? formatPhone(selected.patient_phone) : ''
  const selectedTelHref = selected ? phoneTelHref(selected.patient_phone) : null
  const selectedClientEmails = selected
    ? [...new Set((selected.client_emails ?? []).map((email) => email.trim()).filter(Boolean))]
    : []

  /** null = ainda não resolveu (aguarda atendentes); undefined = todos os profissionais. */
  const professionalUserIdFilter = React.useMemo((): string | undefined | null => {
    if (!canViewValues) {
      return actorId || null
    }
    const email = filterAttendantEmail.trim().toLowerCase()
    if (!email) return undefined
    const match = attendants.find((a) => a.email.trim().toLowerCase() === email)
    return match?.id ?? null
  }, [canViewValues, actorId, filterAttendantEmail, attendants])

  const refreshEvents = React.useCallback(async () => {
    if (!scopeReady || loadingViewValues) return
    setLoading(true)
    setError(null)
    try {
      if (professionalUserIdFilter === null) {
        setEvents([])
        if (!canViewValues) {
          setError('Não foi possível identificar o usuário logado para filtrar a agenda.')
        }
        return
      }

      if (filterStatus.length === 0) {
        setEvents([])
        return
      }

      const { rangeStart, rangeEnd } = getAgendaRange(calendarAnchor, viewMode)
      const rows = await supportCenterAppointmentsService.listByRange({
        range_start: rangeStart,
        range_end: rangeEnd,
        professional_user_id: professionalUserIdFilter,
        specialization_id: canViewValues ? filterSpecializationId || undefined : undefined,
        statuses: filterStatus,
        include_cancelled: filterStatus.includes('cancelado'),
      })
      const enriched = await enrichAgendaEventsFormSubmitted(rows)
      setEvents(enriched)
      const prev = selectedRef.current
      setSelected(prev ? (enriched.find((row) => row.id === prev.id) ?? null) : null)
    } catch (e) {
      setError(getErrorMessage(e))
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [
    scopeReady,
    loadingViewValues,
    canViewValues,
    professionalUserIdFilter,
    filterSpecializationId,
    filterStatus,
    calendarAnchor,
    viewMode,
    refreshKey,
  ])

  React.useEffect(() => {
    void refreshEvents()
  }, [refreshEvents])

  /** Recorta a faixa de horas pelo profissional filtrado (ou própria agenda). */
  React.useEffect(() => {
    if (!scopeReady) return
    if (!canViewValues) {
      const mine = actorId
        ? availabilities.find((row) => row.user_id === actorId)
        : undefined
      const range = resolveAgendaHourRange(mine ? [mine.hours] : [])
      setHourStart(range.start)
      setHourEnd(range.end)
      return
    }
    const email = filterAttendantEmail.trim().toLowerCase()
    if (!email) {
      const range = resolveAgendaHourRange(availabilities.map((row) => row.hours))
      setHourStart(range.start)
      setHourEnd(range.end)
      return
    }
    const attendant = attendants.find((a) => a.email.trim().toLowerCase() === email)
    const avail = attendant
      ? availabilities.find((row) => row.user_id === attendant.id)
      : undefined
    const range = resolveAgendaHourRange(avail ? [avail.hours] : [])
    setHourStart(range.start)
    setHourEnd(range.end)
  }, [
    scopeReady,
    canViewValues,
    actorId,
    filterAttendantEmail,
    attendants,
    availabilities,
  ])

  const singleProfessionalIdForOccupancy = React.useMemo((): string | null => {
    if (!canViewValues) return actorId
    if (professionalUserIdFilter === undefined) return null
    return professionalUserIdFilter
  }, [canViewValues, actorId, professionalUserIdFilter])

  /** Ocupação via API: um profissional (própria agenda ou filtro). */
  React.useEffect(() => {
    if (!scopeReady || loadingViewValues) return
    if (!singleProfessionalIdForOccupancy) return
    let cancelled = false

    const run = async () => {
      const { start, end } = getAgendaRange(calendarAnchor, viewMode)
      const startYmd = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
      const endYmd = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
      const dayCount = viewMode === 'day' ? 1 : 5

      try {
        const slots = await supportCenterAppointmentsService.listAvailableSlots(
          { userId: singleProfessionalIdForOccupancy },
          startYmd,
          endYmd
        )
        if (cancelled) return
        setSlotOccupancy(
          buildOccupancyFromApiSlots(slots, start, hourStart, hourEnd, dayCount)
        )
      } catch {
        if (cancelled) return
        setSlotOccupancy(
          buildOccupancyFromApiSlots([], start, hourStart, hourEnd, dayCount)
        )
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [
    scopeReady,
    loadingViewValues,
    singleProfessionalIdForOccupancy,
    calendarAnchor,
    viewMode,
    hourStart,
    hourEnd,
    refreshKey,
  ])

  /** Ocupação local: visão "Todos" (sem profissional filtrado). */
  React.useEffect(() => {
    if (!scopeReady || loadingViewValues) return
    if (singleProfessionalIdForOccupancy) return

    const { start } = getAgendaRange(calendarAnchor, viewMode)
    const dayCount = viewMode === 'day' ? 1 : 5
    const professionalIds = attendants.map((a) => a.id)
    setSlotOccupancy(
      buildOccupancyLocal({
        rangeStart: start,
        dayCount,
        hourStart,
        hourEnd,
        professionalIds,
        availabilities,
        unavailabilities,
        holidays,
        events,
      })
    )
  }, [
    scopeReady,
    loadingViewValues,
    singleProfessionalIdForOccupancy,
    calendarAnchor,
    viewMode,
    attendants,
    availabilities,
    unavailabilities,
    holidays,
    events,
    hourStart,
    hourEnd,
  ])

  React.useEffect(() => {
    if (loadingViewValues) return
    if (!canViewValues) {
      setFilterAttendantEmail('')
      setFilterSpecializationId('')
      setAttendants([])
      return
    }

    let cancelled = false
    const loadAttendants = async () => {
      try {
        const list = await supportCenterAppointmentsService.listAttendantsForAgenda(
          filterSpecializationId || undefined
        )
        if (cancelled) return
        setAttendants(list)
        setFilterAttendantEmail((prev) =>
          prev && !list.some((attendant) => attendant.email === prev) ? '' : prev
        )
      } catch {
        if (!cancelled) {
          setAttendants([])
          setFilterAttendantEmail('')
        }
      }
    }
    void loadAttendants()
    return () => {
      cancelled = true
    }
  }, [canViewValues, loadingViewValues, filterSpecializationId])

  React.useEffect(() => {
    const allowed = new Set(filterSpecializations.map((spec) => spec.id))
    setFilterSpecializationId((prev) => (prev && !allowed.has(prev) ? '' : prev))
  }, [filterSpecializations])

  const handleSelectEvent = (event: SupportCenterAgendaEvent) => {
    setHistoryOpen(false)
    setCallLinkMenuOpen(false)
    setActionError(null)
    setTriageDetailError(null)
    setAttendStartConfirmOpen(false)
    setSelected(event)
  }

  const closeDetail = React.useCallback(() => {
    setSelected(null)
    setHistoryOpen(false)
    setCallLinkMenuOpen(false)
    setActionError(null)
    setTriageDetailError(null)
    setAttendStartConfirmOpen(false)
    setAttendStartConfirming(false)
  }, [])

  /** Novo agendamento: apenas permissão Criar (insert). */
  const canCreateAppointment = scopeReady && !loadingViewValues && !!canInsert
  const catalogEmpty =
    Boolean(actorId) &&
    scopeReady &&
    !loadingViewValues &&
    formSpecializations.length === 0
  const missingOwnScope = !canViewValues && scopeReady && !loadingViewValues && !actorId

  const displayError = error || scopeError

  const visibleEvents = React.useMemo(() => {
    return events.filter((event) => {
      if (!matchesAgendaTriageFilter(event, filterTriageStatus)) {
        return false
      }
      return matchesAgendaPatientQuery(event, debouncedPatientQuery)
    })
  }, [events, filterTriageStatus, debouncedPatientQuery])

  const periodStatusSummary = React.useMemo(
    () => summarizeAgendaStatuses(visibleEvents),
    [visibleEvents]
  )
  const periodOccupancySummary = React.useMemo(
    () => summarizeSlotOccupancy(slotOccupancy),
    [slotOccupancy]
  )
  const periodLabel = React.useMemo(
    () => formatAgendaLabel(calendarAnchor, viewMode),
    [calendarAnchor, viewMode]
  )
  const occupancyLabel =
    viewMode === 'day' ? 'Ocupação do dia' : 'Ocupação da semana'

  const updateTotalsPopoverPos = React.useCallback(() => {
    const button = totalsButtonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    setTotalsPopoverPos({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    })
  }, [])

  React.useEffect(() => {
    if (!totalsOpen) return
    updateTotalsPopoverPos()
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (totalsButtonRef.current?.contains(target)) return
      if (totalsPopoverRef.current?.contains(target)) return
      setTotalsOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTotalsOpen(false)
    }
    const onReposition = () => updateTotalsPopoverPos()
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [totalsOpen, updateTotalsPopoverPos])

  const overlayBlockingShortcuts =
    formOpen ||
    attendOpen ||
    attendStartConfirmOpen ||
    attendFinalizeConfirmOpen ||
    !!reasonModalKind ||
    detailModalOpen ||
    !!triageCaseModal ||
    historyOpen ||
    !!actionError ||
    !!triageDetailError

  React.useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false
      if (target.isContentEditable) return true
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'))
    }

    const isOverlayTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false
      if (target.closest('[role="listbox"]')) return true
      return Boolean(target.closest('button[aria-expanded="true"]'))
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
      if (isEditableTarget(event.target) || isOverlayTarget(event.target)) return

      if (event.key === 'Escape') {
        if (totalsOpen || callLinkMenuOpen) return
        if (overlayBlockingShortcuts) return
        if (!selected) return
        event.preventDefault()
        closeDetail()
        return
      }

      if (overlayBlockingShortcuts || totalsOpen || callLinkMenuOpen) return

      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key
      if (key === 't') {
        event.preventDefault()
        goToNow()
        return
      }
      if (key === 'd') {
        event.preventDefault()
        setViewMode('day')
        return
      }
      if (key === 's') {
        event.preventDefault()
        setViewMode('week')
        return
      }
      if (key === 'ArrowLeft') {
        event.preventDefault()
        shiftAnchor(-1)
        return
      }
      if (key === 'ArrowRight') {
        event.preventDefault()
        shiftAnchor(1)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    overlayBlockingShortcuts,
    totalsOpen,
    callLinkMenuOpen,
    selected,
    closeDetail,
    goToNow,
    shiftAnchor,
  ])

  React.useEffect(() => {
    setSelected((prev) => {
      if (!prev) return prev
      if (visibleEvents.some((event) => event.id === prev.id)) return prev
      return null
    })
  }, [visibleEvents])

  const handleClearFilters = () => {
    setFilterStatus(DEFAULT_STATUS_FILTER)
    setFilterTriageStatus(DEFAULT_TRIAGE_STATUS_FILTER)
    setFilterPatientQuery('')
    setDebouncedPatientQuery('')
    if (canViewValues) {
      setFilterAttendantEmail('')
      setFilterSpecializationId('')
    }
  }

  React.useEffect(() => {
    if (!onCreateActionChange) return
    onCreateActionChange({
      visible: canCreateAppointment,
      disabled: catalogEmpty || missingOwnScope,
      onClick: () => openCreate(),
    })
    return () => onCreateActionChange(null)
  }, [
    onCreateActionChange,
    canCreateAppointment,
    catalogEmpty,
    missingOwnScope,
    openCreate,
  ])

  React.useEffect(() => {
    if (!onUnavailabilityActionChange) return
    onUnavailabilityActionChange({
      visible: scopeReady && !loadingViewValues,
      disabled: !actorId,
      onClick: () => {
        void openNewUnavail()
      },
    })
    return () => onUnavailabilityActionChange(null)
  }, [
    onUnavailabilityActionChange,
    scopeReady,
    loadingViewValues,
    actorId,
    openNewUnavail,
  ])

  return (
    <>
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
            {canViewValues && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Especialidade
                </label>
                <select
                  className={DASHBOARD_INPUT_CLASS}
                  value={filterSpecializationId}
                  onChange={(e) => {
                    setFilterSpecializationId(e.target.value)
                    setFilterAttendantEmail('')
                  }}
                >
                  <option value="">Todas</option>
                  {filterSpecializations.map((spec) => (
                    <option key={spec.id} value={spec.id}>
                      {spec.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Profissional
              </label>
              {canViewValues ? (
                <select
                  className={DASHBOARD_INPUT_CLASS}
                  value={filterAttendantEmail}
                  onChange={(e) => setFilterAttendantEmail(e.target.value)}
                >
                  <option value="">Todos</option>
                  {attendants.map((attendant) => (
                    <option key={attendant.email} value={attendant.email}>
                      {attendant.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={profile?.name?.trim() || 'Usuário não identificado'}
                  disabled
                  className={`${DASHBOARD_INPUT_CLASS} opacity-70 cursor-not-allowed`}
                  title="Sem permissão de ver valores: filtro travado na sua agenda"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status do Atendimento
              </label>
              <CheckboxMultiSelect
                options={STATUS_FILTER_OPTIONS}
                selected={filterStatus}
                onChange={(selected) =>
                  setFilterStatus(selected as SupportCenterAppointmentStatus[])
                }
                placeholder="Marcado, Reagendado, Realizado, Não realizado"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status da triagem
              </label>
              <CheckboxMultiSelect
                options={TRIAGE_STATUS_FILTER_OPTIONS}
                selected={filterTriageStatus}
                onChange={(selected) =>
                  setFilterTriageStatus(selected as AgendaTriageStatusFilter[])
                }
                placeholder="Sem triagem, Pendente, Concluída, Reprovado, Cancelado, Não Realizado"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Segurado ou Paciente
              </label>
              <input
                type="text"
                value={filterPatientQuery}
                onChange={(e) => setFilterPatientQuery(e.target.value)}
                placeholder="Nome ou CPF..."
                className={DASHBOARD_INPUT_CLASS}
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={handleClearFilters} className="w-full md:w-auto">
                Limpar filtros
              </Button>
            </div>
          </div>
        </div>

        {displayError && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
            {displayError}
          </div>
        )}

        {missingOwnScope ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-12 text-center">
            <Text size="sm" color="muted">
              Não foi possível identificar o usuário logado. Sem a permissão de ver valores, a agenda
              fica restrita aos seus próprios agendamentos.
            </Text>
          </div>
        ) : loading || !scopeReady || loadingViewValues ? (
          <div className="py-20 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.98fr)_minmax(0,1.02fr)] gap-x-4 gap-y-3">
            <div className="min-w-0">
              <AgendaTimeGridView
                days={gridDays}
                events={visibleEvents}
                onSelectEvent={handleSelectEvent}
                selectedEventId={selected?.id ?? null}
                schedulingLimits={schedulingLimits}
                hourStart={hourStart}
                hourEnd={hourEnd}
                slotOccupancy={slotOccupancy}
                paintFullyBookedAsBlocked={
                  !(canViewValues && !filterAttendantEmail.trim())
                }
                scrollToNowToken={scrollToNowToken}
                scrollSessionRef={scrollSessionRef}
                onSlotClick={
                  canCreateAppointment && !catalogEmpty ? (iso) => openCreate(iso) : undefined
                }
                toolbar={(shortcutsButton) => (
                  <div className="min-w-0 flex w-full flex-nowrap items-center gap-2 overflow-x-auto px-1 py-0.5">
                    <div
                      className="inline-flex shrink-0 rounded-lg ring-1 ring-gray-300 dark:ring-white/10 overflow-hidden"
                      role="tablist"
                      aria-label="Visão do calendário"
                    >
                      {(
                        [
                          { id: 'day' as const, label: 'Dia' },
                          { id: 'week' as const, label: 'Semana' },
                        ] as const
                      ).map((option) => {
                        const active = viewMode === option.id
                        return (
                          <button
                            key={option.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setViewMode(option.id)}
                            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                              active
                                ? 'bg-primary-600 text-white dark:bg-primary-700'
                                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/5'
                            }`}
                          >
                            {option.label}
                          </button>
                        )
                      })}
                    </div>
                    <div
                      className="inline-flex shrink-0 rounded-lg ring-1 ring-gray-300 dark:ring-white/10 overflow-hidden"
                      role="group"
                      aria-label="Navegação do calendário"
                    >
                      <button
                        type="button"
                        onClick={() => shiftAnchor(-1)}
                        title={prevNavLabel}
                        aria-label={prevNavLabel}
                        className="px-2.5 py-1.5 text-xs font-medium transition-colors bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/5"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={goToNow}
                        title="Ir para hoje e centralizar o horário atual"
                        aria-label="Ir para hoje e centralizar o horário atual"
                        className="px-3 py-1.5 text-xs font-medium transition-colors bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/5 border-l border-gray-300 dark:border-white/10"
                      >
                        Hoje
                      </button>
                      <button
                        type="button"
                        onClick={() => shiftAnchor(1)}
                        title={nextNavLabel}
                        aria-label={nextNavLabel}
                        className="px-2.5 py-1.5 text-xs font-medium transition-colors bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/5 border-l border-gray-300 dark:border-white/10"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <input
                      type="date"
                      className={`${DASHBOARD_INPUT_CLASS} !w-[8.5rem] !min-w-0 !px-2 shrink-0`}
                      value={`${calendarAnchor.getFullYear()}-${String(calendarAnchor.getMonth() + 1).padStart(2, '0')}-${String(calendarAnchor.getDate()).padStart(2, '0')}`}
                      onChange={(e) => {
                        const value = e.target.value
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return
                        setCalendarAnchor(new Date(`${value}T12:00:00`))
                      }}
                      title={goToDateTitle}
                      aria-label={goToDateTitle}
                    />
                    <div className="ml-auto inline-flex shrink-0 items-center gap-2">
                      {shortcutsButton}
                      <button
                        type="button"
                        ref={totalsButtonRef}
                        onClick={() => {
                          setTotalsOpen((prev) => {
                            const next = !prev
                            if (next) {
                              requestAnimationFrame(() => updateTotalsPopoverPos())
                            }
                            return next
                          })
                        }}
                        title="Totais do período"
                        aria-label="Totais do período"
                        aria-expanded={totalsOpen}
                        aria-haspopup="dialog"
                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ring-1 ${
                          totalsOpen
                            ? 'bg-primary-600 text-white ring-primary-600 dark:bg-primary-700 dark:ring-primary-700'
                            : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-white/5'
                        }`}
                      >
                        <ChartBarIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        Totais
                      </button>
                      <button
                        type="button"
                        onClick={refreshAgenda}
                        disabled={loading}
                        title={loading ? 'Atualizando a agenda' : 'Atualizar a agenda'}
                        aria-label={loading ? 'Atualizando a agenda' : 'Atualizar a agenda'}
                        className="inline-flex items-center justify-center rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ring-1 bg-primary-600 text-white ring-primary-600 hover:bg-primary-700 disabled:opacity-60 dark:bg-primary-700 dark:text-white dark:ring-primary-700 dark:hover:bg-primary-600"
                      >
                        <ArrowPathIcon
                          className={`h-4 w-4 shrink-0 ${loading ? 'animate-spin' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                )}
              />
            </div>
            <div
              className={`bg-white dark:bg-gray-800 rounded-lg shadow h-fit overflow-hidden ${
                selected
                  ? 'xl:sticky xl:top-0 xl:z-10 xl:self-start xl:max-h-[calc(100vh-1.5rem)] xl:overflow-y-auto'
                  : ''
              }`}
            >
              <div className="relative p-4">
              {selected ? (
                <span
                  className={`absolute inset-y-0 left-0 w-1.5 ${agendaEventSwatchClass(
                    selected
                  )}`}
                  aria-hidden="true"
                />
              ) : null}
              <div className={selected ? 'pl-2' : undefined}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  {selected ? (
                    <span
                      className={`h-4 w-4 rounded-sm shrink-0 ring-1 ring-black/10 dark:ring-white/20 ${agendaEventSwatchClass(
                        selected
                      )}`}
                      title={agendaEventColorLabel(selected)}
                      aria-label={`Cor do agendamento: ${agendaEventColorLabel(selected)}`}
                    />
                  ) : null}
                  <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
                    Detalhe
                  </Heading>
                </div>
                {selected ? (
                  <div className="text-right shrink-0">
                    <Text size="xs" color="muted">
                      Quando
                    </Text>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatDateTime(selected.scheduled_at)}
                    </div>
                  </div>
                ) : null}
              </div>
              {!selected ? (
                <Text size="sm" color="muted">
                  Selecione um evento no calendário.
                </Text>
              ) : (
                <div className="space-y-4 text-sm">
                  <section className="space-y-3">
                    <Text
                      size="xs"
                      className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300"
                    >
                      Paciente
                    </Text>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {selected.patient_people?.name || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Text size="xs" color="muted">
                          CPF
                        </Text>
                        <div className="text-gray-900 dark:text-white">
                          {selected.patient_cpf ? formatCpf(selected.patient_cpf) : '—'}
                        </div>
                      </div>
                      <div>
                        <Text size="xs" color="muted">
                          Telefone
                        </Text>
                        {selectedPhoneLabel && selectedTelHref ? (
                          <a
                            href={selectedTelHref}
                            className="text-primary-600 hover:underline dark:text-primary-400 break-all"
                          >
                            {selectedPhoneLabel}
                          </a>
                        ) : (
                          <div className="text-gray-900 dark:text-white">
                            {selectedPhoneLabel || '—'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Text size="xs" color="muted">
                        E-mails
                      </Text>
                      {selectedClientEmails.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {selectedClientEmails.map((email) => (
                            <a
                              key={email}
                              href={`mailto:${email}`}
                              className="text-primary-600 hover:underline dark:text-primary-400 break-all"
                            >
                              {email}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <div className="text-gray-900 dark:text-white">—</div>
                      )}
                    </div>
                  </section>

                  <section className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                    <Text
                      size="xs"
                      className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300"
                    >
                      Agendamento
                    </Text>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-3">
                        <div>
                          <Text size="xs" color="muted">
                            Status
                          </Text>
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${appointmentStatusBadgeClass(
                              selected.status
                            )}`}
                          >
                            {APPOINTMENT_STATUS_LABELS[selected.status]}
                          </span>
                        </div>
                        <div>
                          <Text size="xs" color="muted">
                            Marcado em
                          </Text>
                          <div className="text-gray-900 dark:text-white">
                            {formatDateTimeLines(selected.created_at)?.date ?? '—'}
                          </div>
                        </div>
                      </div>
                      {selected.status !== 'pre_agendado' ? (
                        <div className="space-y-3">
                          <div>
                            <Text size="xs" color="muted">
                              Triagem
                            </Text>
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${triageColumnBadgeClass(
                                selected.triage_status ??
                                  (selected.triage_id ? 'pendente' : null)
                              )}`}
                            >
                              {selected.triage_status
                                ? TRIAGE_STATUS_LABELS[selected.triage_status]
                                : selected.triage_id
                                  ? TRIAGE_STATUS_LABELS.pendente
                                  : 'Sem triagem'}
                            </span>
                          </div>
                          {selected.triage_status === 'concluida' && (
                            <div>
                              <Text size="xs" color="muted">
                                Data da triagem
                              </Text>
                              <div className="text-gray-900 dark:text-white">
                                {formatDateOnlyPtBr(selected.triage_date)}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Text size="xs" color="muted">
                          Especialidade
                        </Text>
                        <div className="text-gray-900 dark:text-white">
                          {selected.specialization?.name || '—'}
                        </div>
                      </div>
                      <div>
                        <Text size="xs" color="muted">
                          Profissional
                        </Text>
                        <div className="text-gray-900 dark:text-white">
                          {selected.professional?.name || '—'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Text size="xs" color="muted">
                          Assistência
                        </Text>
                        <div className="text-gray-900 dark:text-white">
                          {selected.assistance
                            ? `${selected.assistance.name} (${selected.assistance.code})`
                            : '—'}
                        </div>
                      </div>
                      <div>
                        <Text size="xs" color="muted">
                          Formulário
                        </Text>
                        {selectedFormStatus ? (
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${attendanceFormStatusBadgeClass(
                              selectedFormStatus
                            )}`}
                          >
                            {ATTENDANCE_FORM_STATUS_LABELS[selectedFormStatus]}
                          </span>
                        ) : (
                          <div className="text-gray-900 dark:text-white">—</div>
                        )}
                      </div>
                    </div>
                    {(selected.attendance_started_at || selected.attendance_completed_at) && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Text size="xs" color="muted">
                            Início do atendimento
                          </Text>
                          <div className="text-gray-900 dark:text-white">
                            {selected.attendance_started_at
                              ? formatDateTime(selected.attendance_started_at)
                              : '—'}
                          </div>
                        </div>
                        <div>
                          <Text size="xs" color="muted">
                            Fim do atendimento
                          </Text>
                          <div className="text-gray-900 dark:text-white">
                            {selected.attendance_completed_at
                              ? formatDateTime(selected.attendance_completed_at)
                              : '—'}
                          </div>
                        </div>
                      </div>
                    )}
                    <AppointmentAttachmentsList
                      key={selected.id}
                      appointmentId={selected.id}
                      canUpload={!!canUpdate}
                    />
                    {selected.status === 'cancelado' && (
                      <div className="space-y-2">
                        <div>
                          <Text size="xs" color="muted">
                            Canal do cancelamento
                          </Text>
                          <div className="text-gray-900 dark:text-white">
                            {appointmentChannelLabel(selected.cancellation_channel)}
                          </div>
                        </div>
                        <div>
                          <Text size="xs" color="muted">
                            Motivo do cancelamento
                          </Text>
                          <div className="text-gray-900 dark:text-white">
                            {selected.cancel_reason?.name || '—'}
                          </div>
                          {selected.cancel_reason?.description ? (
                            <Text size="xs" color="muted" className="mt-0.5">
                              {selected.cancel_reason.description}
                            </Text>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </section>

                  <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                    {attendanceAlreadyDone && selectedFormKind && (
                      <div className="space-y-2">
                        <Text
                          size="xs"
                          className="font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                        >
                          Fluxo
                        </Text>
                        <Button
                          variant="outline"
                          onClick={openAttendanceForm}
                          title={
                            formAlreadySubmitted
                              ? 'Formulário concluído. Abre em somente leitura.'
                              : 'Abre o formulário do atendimento para preencher ou concluir'
                          }
                          className={
                            formAlreadySubmitted
                              ? DETAIL_ACTION_SUCCESS_CLASS
                              : DETAIL_ACTION_OUTLINE_CLASS
                          }
                        >
                          {formAlreadySubmitted ? (
                            <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          ) : (
                            <ClipboardDocumentListIcon
                              className="h-4 w-4 shrink-0"
                              aria-hidden="true"
                            />
                          )}
                          {formAlreadySubmitted
                            ? 'Formulário concluído'
                            : 'Formulário do atendimento'}
                        </Button>
                      </div>
                    )}
                    {canRescheduleSelected && (
                      <div className="space-y-2">
                        <Text
                          size="xs"
                          className="font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                        >
                          Fluxo
                        </Text>
                        <div className="flex flex-col gap-2">
                          {canRealizarTriagem && (
                            <Button
                              onClick={() => void openRealizarTriagem()}
                              disabled={triageCaseLoading}
                              className={DETAIL_ACTION_SOLID_CLASS}
                            >
                              {triageCaseLoading ? (
                                <ArrowPathIcon
                                  className="h-4 w-4 shrink-0 animate-spin"
                                  aria-hidden="true"
                                />
                              ) : (
                                <ClipboardDocumentCheckIcon
                                  className="h-4 w-4 shrink-0"
                                  aria-hidden="true"
                                />
                              )}
                              {triageCaseLoading ? 'Carregando triagem...' : 'Realizar triagem'}
                            </Button>
                          )}
                          {triageApproved && selected.triage_id && (
                            <Button
                              variant="outline"
                              onClick={() => void openTriageDetail()}
                              disabled={triageDetailLoading}
                              className={DETAIL_ACTION_OUTLINE_CLASS}
                            >
                              {triageDetailLoading ? (
                                <ArrowPathIcon
                                  className="h-4 w-4 shrink-0 animate-spin"
                                  aria-hidden="true"
                                />
                              ) : (
                                <EyeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                              )}
                              {triageDetailLoading ? 'Carregando triagem...' : 'Ver triagem'}
                            </Button>
                          )}
                          {canAttendFlowSelected && (
                            <>
                              <Button
                                variant={
                                  attendActionLabel === 'Finalizar atendimento' || triageApproved
                                    ? 'solid'
                                    : 'outline'
                                }
                                onClick={openAttendPrimaryAction}
                                title={
                                  selected.attendance_started_at
                                    ? 'Marca o atendimento como realizado'
                                    : 'Registra o início do atendimento'
                                }
                                className={
                                  attendActionLabel === 'Finalizar atendimento'
                                    ? DETAIL_ACTION_DANGER_SOLID_CLASS
                                    : triageApproved
                                      ? DETAIL_ACTION_SOLID_CLASS
                                      : DETAIL_ACTION_OUTLINE_CLASS
                                }
                              >
                                <DocumentTextIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                {attendActionLabel}
                              </Button>
                              {selectedFormKind && (
                                <Button
                                  variant="outline"
                                  onClick={openAttendanceForm}
                                  title={
                                    formAlreadySubmitted
                                      ? 'Formulário concluído. Abre em somente leitura.'
                                      : 'Disponível após iniciar o atendimento'
                                  }
                                  className={
                                    formAlreadySubmitted
                                      ? DETAIL_ACTION_SUCCESS_CLASS
                                      : DETAIL_ACTION_OUTLINE_CLASS
                                  }
                                >
                                  {formAlreadySubmitted ? (
                                    <CheckCircleIcon
                                      className="h-4 w-4 shrink-0"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <ClipboardDocumentListIcon
                                      className="h-4 w-4 shrink-0"
                                      aria-hidden="true"
                                    />
                                  )}
                                  {formAlreadySubmitted
                                    ? 'Formulário concluído'
                                    : 'Formulário do atendimento'}
                                </Button>
                              )}
                            </>
                          )}
                          {selected.status === 'reagendado' && (
                            <Text size="sm" color="muted">
                              {attendanceRescheduledMsg}
                            </Text>
                          )}
                        </div>
                      </div>
                    )}

                    {(canRescheduleSelected ||
                      canCancelSelected ||
                      canCancelRealizadoSelected ||
                      canConfirmPreBookingSelected ||
                      canDiscardPreBookingSelected ||
                      (selectedFormKind && selected.patient_cpf)) && (
                      <div className="space-y-2">
                        <Text
                          size="xs"
                          className="font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                        >
                          Ações
                        </Text>
                        <div
                          className={
                            detailActionCount <= 1
                              ? 'grid grid-cols-1 gap-2'
                              : 'grid grid-cols-2 gap-2'
                          }
                        >
                          {canConfirmPreBookingSelected && (
                            <Button
                              variant="outline"
                              onClick={() => openConfirmPreBooking(selected)}
                              className={DETAIL_ACTION_RESCHEDULE_CLASS}
                            >
                              <CalendarDaysIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                              Completar pré
                            </Button>
                          )}
                          {canRescheduleSelected && (
                            <Button
                              variant="outline"
                              onClick={() => openEdit(selected)}
                              className={DETAIL_ACTION_RESCHEDULE_CLASS}
                            >
                              <CalendarDaysIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                              Reagendar
                            </Button>
                          )}
                          {canRescheduleSelected && (
                            <div className="relative" ref={callLinkMenuRef}>
                              <Button
                                variant="outline"
                                onClick={toggleCallLinkMenu}
                                aria-expanded={callLinkMenuOpen}
                                aria-haspopup="menu"
                                title="Abrir ou copiar o link da reunião"
                                className={DETAIL_ACTION_LINK_CLASS}
                              >
                                <VideoCameraIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                Link
                                <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                              </Button>
                              {callLinkMenuOpen && (
                                <div
                                  role="menu"
                                  className="absolute left-0 right-0 z-20 mt-1 origin-top rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 py-1"
                                >
                                  <button
                                    type="button"
                                    role="menuitem"
                                    onClick={openCallLink}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-primary-50 hover:text-primary-800 dark:text-gray-200 dark:hover:bg-primary-950/40 dark:hover:text-primary-200"
                                  >
                                    <VideoCameraIcon
                                      className="h-4 w-4 shrink-0"
                                      aria-hidden="true"
                                    />
                                    Abrir em nova aba
                                  </button>
                                  <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => void copyCallLink()}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-primary-50 hover:text-primary-800 dark:text-gray-200 dark:hover:bg-primary-950/40 dark:hover:text-primary-200"
                                  >
                                    <ClipboardDocumentIcon
                                      className="h-4 w-4 shrink-0"
                                      aria-hidden="true"
                                    />
                                    Copiar link
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {historyActionVisible && selectedFormKind && (
                            <Button
                              variant="outline"
                              onClick={openHistory}
                              title={`Ver histórico ${SUPPORT_CENTER_FORM_KIND_LABELS[selectedFormKind].toLowerCase()}`}
                              className={DETAIL_ACTION_HISTORY_CLASS}
                            >
                              <ClipboardDocumentListIcon
                                className="h-4 w-4 shrink-0"
                                aria-hidden="true"
                              />
                              Histórico
                            </Button>
                          )}
                          {(canCancelSelected || canCancelRealizadoSelected) && (
                            <Button
                              variant="outline"
                              onClick={openCancelModal}
                              className={DETAIL_ACTION_DANGER_CLASS}
                            >
                              <XCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                              Cancelar
                            </Button>
                          )}
                          {canDiscardPreBookingSelected && (
                            <Button
                              variant="outline"
                              onClick={openDiscardPreConfirm}
                              className={DETAIL_ACTION_DANGER_CLASS}
                            >
                              <XCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                              Descartar pré
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {selected.status === 'pre_agendado' && (
                      <Text size="xs" color="muted">
                        Pré-agendamento: horário reservado. Complete os dados para confirmar.
                      </Text>
                    )}
                    {selected.status === 'realizado' && (
                      <Text size="xs" color="muted">
                        {canCancelRealizadoSelected
                          ? 'Atendimento marcado como realizado. Cancelamento disponível: formulário não enviado.'
                          : selected.attendance_form_submitted
                            ? 'Atendimento já marcado como realizado. Formulário enviado — cancelamento indisponível.'
                            : 'Atendimento já marcado como realizado.'}
                      </Text>
                    )}
                    {selected.status === 'nao_realizado' && (
                      <Text size="xs" color="muted">
                        Atendimento marcado como Não Realizado.
                      </Text>
                    )}
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={closeDetail}
                      className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
                      title="Ocultar detalhe"
                    >
                      <EyeSlashIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      Ocultar
                    </button>
                  </div>
                </div>
              )}
              </div>
              </div>
            </div>
          </div>
          </>
        )}
      </div>

      <AppointmentFormModal
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        initialScheduledAt={slotScheduledAt}
        specializations={formSpecializations}
        userSpecializationIds={canViewValues ? [] : userSpecializationIds}
        restrictToOwnProfessional={!canViewValues}
        currentUserId={actorId}
        canSubmit={formMode === 'create' ? canCreateAppointment : !!canUpdate}
        onClose={() => setFormOpen(false)}
        onSaved={(event) => void handleSaved(event)}
      />

      {unavailModal && (
        <ParametrosUnavailModal
          unavailModal={unavailModal}
          unavailError={unavailError}
          submitting={submittingUnavail}
          assignedProfileOptions={unavailProfileOptions}
          lockProfessional={!canViewValues}
          onChange={setUnavailModal}
          onClose={closeUnavailModal}
          onSave={() => void handleUnavailSave()}
        />
      )}

      <AppointmentAttendModal
        open={attendOpen}
        appointment={selected}
        canUpdate={!!canUpdate}
        onClose={() => setAttendOpen(false)}
        onDraftSaved={() => {
          void logUpdate(
            'support_center_appointment',
            selected?.id ?? null,
            'Salvou rascunho do formulário de atendimento pela agenda da Central de Amparo'
          )
        }}
        onFormSubmitted={() => {
          const id = selected?.id
          void logUpdate(
            'support_center_appointment',
            id ?? null,
            'Enviou formulário de atendimento pela agenda da Central de Amparo'
          )
          if (!id) return
          const markSubmitted = <T extends { id: string; attendance_form_submitted?: boolean | null }>(
            event: T
          ): T =>
            event.id === id ? { ...event, attendance_form_submitted: true } : event
          setSelected((prev) => (prev ? markSubmitted(prev) : prev))
          setEvents((prev) => prev.map(markSubmitted))
        }}
      />

      {discardPreConfirmOpen &&
        selected &&
        createPortal(
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Descartar pré-agendamento
              </Heading>
              <Text size="sm" color="muted">
                Ao confirmar, o horário reservado para{' '}
                <span className="font-medium text-gray-900 dark:text-white">
                  {selected.patient_people?.name ||
                    selected.holder_people?.name ||
                    formatCpf(selected.patient_cpf || selected.holder_cpf)}
                </span>{' '}
                será liberado. Deseja continuar?
              </Text>
              {discardPreError && (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                  {discardPreError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDiscardPreConfirmOpen(false)
                    setDiscardPreError(null)
                  }}
                  disabled={discardPreConfirming}
                >
                  Voltar
                </Button>
                <Button
                  onClick={() => void handleDiscardPreBooking()}
                  disabled={discardPreConfirming}
                >
                  {discardPreConfirming ? 'Descartando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {attendStartConfirmOpen &&
        createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Iniciar atendimento
            </Heading>
            <Text size="sm" color="muted">
              Ao confirmar, o horário de início do atendimento será gravado
              {attendStartRecordedAt ? (
                <>
                  {' '}
                  como{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDateTime(attendStartRecordedAt)}
                  </span>
                </>
              ) : null}
              . Deseja continuar?
            </Text>
            {attendStartError && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                {attendStartError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setAttendStartConfirmOpen(false)
                  setAttendStartError(null)
                }}
                disabled={attendStartConfirming}
              >
                Voltar
              </Button>
              <Button
                onClick={() => void confirmAttendFormStart()}
                disabled={attendStartConfirming}
              >
                {attendStartConfirming ? 'Gravando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
        )}

      {attendFinalizeConfirmOpen &&
        createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Finalizar atendimento
            </Heading>
            <Text size="sm" color="muted">
              Ao confirmar, o atendimento será marcado como realizado e o horário de fim será
              gravado
              {attendFinalizeRecordedAt ? (
                <>
                  {' '}
                  como{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDateTime(attendFinalizeRecordedAt)}
                  </span>
                </>
              ) : null}
              . Deseja continuar?
            </Text>
            {attendFinalizeError && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                {attendFinalizeError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setAttendFinalizeConfirmOpen(false)
                  setAttendFinalizeError(null)
                }}
                disabled={attendFinalizeConfirming}
              >
                Voltar
              </Button>
              <Button
                onClick={() => void confirmAttendFinalize()}
                disabled={attendFinalizeConfirming}
              >
                {attendFinalizeConfirming ? 'Finalizando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
        )}

      {totalsOpen &&
        totalsPopoverPos &&
        createPortal(
          <div
            ref={totalsPopoverRef}
            role="dialog"
            aria-label="Totais do período"
            className="fixed z-[100] w-72 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 p-4 space-y-3"
            style={{ top: totalsPopoverPos.top, right: totalsPopoverPos.right }}
          >
            <div>
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                Totais do período
              </Heading>
              <Text size="xs" color="muted" className="mt-0.5">
                {periodLabel}
              </Text>
            </div>

            <ul className="space-y-1.5">
              {(Object.keys(APPOINTMENT_STATUS_LABELS) as SupportCenterAppointmentStatus[]).map(
                (status) => (
                  <li key={status} className="flex items-center justify-between gap-2 text-sm">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${agendaEventAppointmentBadgeClass(
                        status
                      )}`}
                    >
                      {APPOINTMENT_STATUS_LABELS[status]}
                    </span>
                    <span className="font-medium tabular-nums text-gray-900 dark:text-white">
                      {periodStatusSummary.byStatus[status]}
                    </span>
                  </li>
                )
              )}
              <li className="flex items-center justify-between gap-2 text-sm border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-1">
                <span className="font-semibold text-gray-900 dark:text-white">Total</span>
                <span className="font-semibold tabular-nums text-gray-900 dark:text-white">
                  {periodStatusSummary.total}
                </span>
              </li>
            </ul>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                  {occupancyLabel}
                </Text>
                <Text size="sm" className="tabular-nums text-gray-700 dark:text-gray-200">
                  {periodOccupancySummary.rate == null
                    ? '—'
                    : `${Math.round(periodOccupancySummary.rate * 100)}%`}
                </Text>
              </div>
              <Text size="xs" color="muted">
                {periodOccupancySummary.capacity === 0
                  ? 'Sem horários disponíveis no período.'
                  : `${periodOccupancySummary.occupied} de ${periodOccupancySummary.capacity} horários ocupados`}
              </Text>
              {periodOccupancySummary.capacity > 0 && (
                <div
                  className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round((periodOccupancySummary.rate ?? 0) * 100)}
                  aria-label={occupancyLabel}
                >
                  <div
                    className="h-full rounded-full bg-primary-600 dark:bg-primary-500 transition-[width]"
                    style={{
                      width: `${Math.min(100, Math.round((periodOccupancySummary.rate ?? 0) * 100))}%`,
                    }}
                  />
                </div>
              )}
            </div>

            <Text size="xs" color="muted">
              Considera os filtros aplicados.
            </Text>
          </div>,
          document.body
        )}

      <AgendaAttentionModal
        message={actionError || triageDetailError}
        onClose={() => {
          setActionError(null)
          setTriageDetailError(null)
        }}
      />

      {detailModalOpen && (
        <TriagemDetailModal
          triage={triageDetail}
          appointmentHint={detailAppointmentHint}
          canUpdate={!!canUpdate}
          onClose={closeTriageDetail}
        />
      )}

      {triageCaseModal && (
        <TriagemCaseModal
          triageCase={triageCaseModal}
          canUpdate={!!canUpdate}
          profiles={triageProfiles}
          referralOrigins={triageReferralOrigins}
          demandTypes={triageDemandTypes}
          pastToleranceMinutes={pastToleranceMinutes}
          onClose={closeTriageCaseModal}
          onSaved={handleTriageCaseSaved}
        />
      )}

      {selectedFormKind && selected?.scheduled_at && (
        <AppointmentPatientHistoryModal
          open={historyOpen}
          patientCpf={selected.patient_cpf}
          patientName={selected.patient_people?.name || undefined}
          formKind={selectedFormKind}
          untilScheduledAt={selected.scheduled_at}
          onClose={() => setHistoryOpen(false)}
          onSelectRow={(row) => void openHistoryRowDetail(row)}
        />
      )}

      {reasonModalKind && selected && (
        <AgendaCancelModal
          patientLabel={selected.patient_people?.name || selected.patient_cpf}
          cancelReasonId={cancelReasonId}
          cancelReasons={cancelReasons}
          loadingCancelReasons={loadingCancelReasons}
          cancelling={cancelling}
          cancelError={cancelError}
          onReasonChange={setCancelReasonId}
          cancellationChannel={cancellationChannel}
          onCancellationChannelChange={setCancellationChannel}
          onClose={closeCancelModal}
          onConfirm={() => void handleCancelConfirm()}
        />
      )}
    </>
  )
}

export default CalendarioAgendaTab
